import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useStudentAuth } from "@/frontend/contexts/StudentAuthContext";
import { useCurrentStudent } from "@/backend/hooks/useStudent";
import { useMyContract } from "@/backend/hooks/useMyContract";
import { updateStudent } from "@/backend/services/students";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/frontend/components/ui/card";
import { Input } from "@/frontend/components/ui/input";
import { Button } from "@/frontend/components/ui/button";
import { useToast } from "@/backend/hooks/use-toast";
import { User, Pencil, Check, X, Loader2 } from "lucide-react";

const StudentProfile = () => {
  const { user } = useStudentAuth();
  const { data: student } = useCurrentStudent(user?.uid);
  const { data: contract } = useMyContract();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [phoneValue, setPhoneValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleEditPhone = () => {
    setPhoneValue(student?.phone ?? "");
    setIsEditingPhone(true);
  };

  const handleCancelEdit = () => {
    setIsEditingPhone(false);
    setPhoneValue("");
  };

  const handleSavePhone = async () => {
    if (!user?.uid) return;

    const trimmed = phoneValue.trim();
    if (!trimmed || trimmed.length < 9) {
      toast({
        title: "Invalid phone number",
        description: "Phone number must have at least 9 characters.",
        variant: "destructive",
      });
      return;
    }

    if (!/^\+?[\d\s\-()]+$/.test(trimmed)) {
      toast({
        title: "Invalid phone number",
        description: "Phone number contains invalid characters.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      await updateStudent(user.uid, { phone: trimmed });
      await queryClient.invalidateQueries({ queryKey: ["students", user.uid] });
      toast({
        title: "Phone updated",
        description: "Your phone number has been updated successfully.",
      });
      setIsEditingPhone(false);
    } catch {
      toast({
        title: "Error",
        description: "Failed to update phone number. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold">My Profile</h2>
        <p className="text-muted-foreground mt-1">Manage your personal information</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Personal Information
          </CardTitle>
          <CardDescription>Your profile details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground">Name</p>
            <p className="font-medium">{student?.name}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Email</p>
            <p className="font-medium">{student?.email}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Phone</p>
            {isEditingPhone ? (
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="tel"
                  value={phoneValue}
                  onChange={(e) => setPhoneValue(e.target.value)}
                  placeholder="+351 XXX XXX XXX"
                  className="max-w-xs"
                  disabled={isSaving}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSavePhone();
                    if (e.key === "Escape") handleCancelEdit();
                  }}
                  autoFocus
                />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={handleSavePhone}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 text-green-600" />
                  )}
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                >
                  <X className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <p className="font-medium">{student?.phone}</p>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={handleEditPhone}
                >
                  <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              </div>
            )}
          </div>
          {contract?.nif && (
            <div>
              <p className="text-sm text-muted-foreground">NIF (Tax Payer Number)</p>
              <p className="font-medium">{contract.nif}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentProfile;
